import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { documentsApi } from '@/features/customers/lib/documents-api';

/**
 * `/staff/documents/:documentId` — a durable link that opens one document.
 *
 * This exists so a document can be linked to from OUTSIDE the console: the Apply Coupon card
 * writes this URL into the Stripe customer's metadata, so someone looking at an unexplained
 * discount can click through to the tax-exemption certificate that justified it.
 *
 * Why not link R2 directly? A presigned URL is the only way bytes leave the private bucket, and
 * it is both short-lived (`X-Amz-Expires=300`) and a bearer token (`X-Amz-Signature`). Stored
 * in a third-party system it would be broken within five minutes AND would hand credential-grade
 * access to anyone who could read it. So the durable link points HERE, and the presigned URL is
 * minted per click, behind the operator's own session, and never persisted anywhere.
 *
 * The redirect is deliberately `location.replace` rather than a new tab: this page is a way
 * station, and leaving it in history would send Back to a URL that immediately re-resolves.
 */
export function DocumentResolvePage() {
  const { documentId } = useParams();
  const [error, setError] = useState<string | null>(null);
  // StrictMode double-invokes effects in dev; minting two presigned URLs would file two
  // `presign_download` ledger rows for one click and make the access log lie.
  const started = useRef(false);

  useEffect(() => {
    if (!documentId || started.current) return;
    started.current = true;
    void (async () => {
      try {
        const { url } = await documentsApi.download(documentId, 'inline');
        window.location.replace(url);
      } catch (e: unknown) {
        setError((e as Error)?.message ?? 'Could not open this document.');
      }
    })();
  }, [documentId]);

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-8 text-center">
      {error ? (
        <>
          <h1 className="text-lg font-semibold" data-testid="document-resolve-error">
            {'Could not open this document'}
          </h1>
          <p className="max-w-md text-sm text-muted-foreground">{error}</p>
          <p className="max-w-md text-xs text-muted-foreground">
            {
              'If it was deleted, the link outlives the file on purpose — the access log still records who filed and who removed it.'
            }
          </p>
        </>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid="document-resolve-loading">
          {'Opening document…'}
        </p>
      )}
    </div>
  );
}
